<template>
  <section class="section">
    <div class="container">
      <h1 class="title">
        {{ $translate('models') | capitalize }}
      </h1>
      <div class="level">
        <div class="level-left">
          <div class="level-item">
            <b-button
              type="is-primary"
              icon-right="file-pdf"
              :disabled="tableOptions.checkedRows.length == 0"
              @click="exportPdfTable(module)"
            ></b-button>
          </div>

          <div class="level-item">
            <b-button
              v-if="canManage"
              type="is-primary"
              icon-right="plus"
              @click="
                $router.push({
                  name: 'newMod',
                  params: { module },
                })
              "
            ></b-button>
          </div>
        </div>
        <div class="level-right">
          <b-field>
            <b-input
              v-model="queryString"
              :placeholder="$translate('search').concat('...') | capitalize"
              type="search"
              icon="magnify"
              @keyup.native.enter="searchData()"
            ></b-input>
          </b-field>
        </div>
      </div>
      <b-table
        :sticky-header="true"
        :data="tableData"
        :narrowed="true"
        :checked-rows.sync="tableOptions.checkedRows"
        :sort-icon="tableOptions.sortIcon"
        :sort-icon-size="tableOptions.sortIconSize"
        :loading="tableOptions.loading"
        :backend-sorting="true"
        :row-class="(row, index) => row.draft && 'is-draft'"
        checkable
        @sort="sortPressed"
      >
        <template slot-scope="props">
          <b-table-column field="position" :label="$translate('model_position') | capitalize" width="70px" numeric>
            <span class="is-text">{{ props.row.position }}</span>
          </b-table-column>
          <b-table-column field="name" :label="$translate('model') | capitalize" sortable>
            <span class="is-text">{{ props.row.name }}</span>
          </b-table-column>
          <b-table-column
            field="modelImage"
            :label="$translate('image') | capitalize"
            :width="module === 'insole' ? '90px' : null"
          >
            <img :src="props.row.image" :width="module === 'insole' ? '70px' : '100px'" height="auto" />
          </b-table-column>
          <template v-if="$moduleConfiguration[module].minPel === $moduleConfiguration[module].maxPel">
            <b-table-column
              v-for="i in $moduleConfiguration[module].maxPel"
              :key="`pell${i}var`"
              :field="`pell${i}var`"
              :label="$translate(`pell${i}title`) | capitalize"
            >
              <b-tooltip :label="props.row[`pell${i}tag`]" position="is-left">
                <img :src="props.row[`pellImage${i}`]" width="75px" height="auto" />
              </b-tooltip>
            </b-table-column>
          </template>
          <template v-else>
            <b-table-column width="200px" field="pellami" :label="$translate(`pellami`) | capitalize">
              <div class="pellami-container">
                <b-tooltip
                  v-for="i in props.row.pelCount"
                  :key="`pell${i}var`"
                  :label="props.row[`pell${i}tag`]"
                  position="is-left"
                >
                  <img
                    :src="props.row[`pellImage${i}`]"
                    width="50px"
                    height="auto"
                    @click="() => showImageModal(props.row[`pellImage${i}`], props.row[`pell${i}tag`])"
                  />
                </b-tooltip>
              </div>
            </b-table-column>
            <b-table-column
              v-if="$moduleConfiguration[module].modelHasRiporti"
              width="200px"
              field="riporti"
              :label="$translate(`riporti`) | capitalize"
            >
              <div class="pellami-container">
                <b-tooltip
                  v-for="i in props.row.ripCount"
                  :key="`rip${i}var`"
                  :label="props.row[`rip${i}tag`]"
                  position="is-left"
                >
                  <img
                    :src="props.row[`ripImage${i}`]"
                    width="50px"
                    height="auto"
                    @click="() => showImageModal(props.row[`ripImage${i}`], props.row[`rip${i}tag`])"
                  />
                </b-tooltip>
              </div>
            </b-table-column>
          </template>
          <template v-if="module === 'tailored'">
            <b-table-column field="fodantvar" :label="$translate('fodanttitle') | capitalize">
              <b-tooltip :label="props.row.fod1tag" position="is-left">
                <img :src="props.row.fodImage1" width="75px" height="auto" />
              </b-tooltip>
            </b-table-column>
            <b-table-column field="fodposvar" :label="$translate('fodpostitle') | capitalize">
              <b-tooltip :label="props.row.fod2tag" position="is-left">
                <img :src="props.row.fodImage2" width="75px" height="auto" />
              </b-tooltip>
            </b-table-column>
            <b-table-column field="battvar" :label="$translate('batttitle') | capitalize">
              <b-tooltip :label="props.row.battag" position="is-left">
                <img :src="props.row.batImage" width="75px" height="auto" />
              </b-tooltip>
            </b-table-column>
            <b-table-column field="intevar" :label="$translate('intetitle') | capitalize">
              <b-tooltip :label="props.row.inttag" position="is-left">
                <img :src="props.row.intImage" width="75px" height="auto" />
              </b-tooltip>
            </b-table-column>
          </template>
          <template v-if="module === 'insole'">
            <b-table-column width="160px" centered field="insoleBase" :label="$translate('insoleBaseMat') | capitalize">
              <b-tooltip :label="props.row.insoleBaseTag" position="is-left">
                <img
                  v-if="props.row.insoleBaseImage"
                  :src="props.row.insoleBaseImage"
                  width="75px"
                  height="auto"
                  @click="() => showImageModal(props.row.insoleBaseImage, props.row.insoleBaseTag)"
                />
              </b-tooltip>
            </b-table-column>
            <b-table-column
              width="160px"
              centered
              field="insoleCover"
              :label="$translate('insoleCoverMat') | capitalize"
            >
              <b-tooltip :label="props.row.insoleCoverTag" position="is-left">
                <img
                  v-if="props.row.insoleCoverImage"
                  :src="props.row.insoleCoverImage"
                  width="75px"
                  height="auto"
                  @click="() => showImageModal(props.row.insoleCoverImage, props.row.insoleCoverTag)"
                />
              </b-tooltip>
            </b-table-column>
            <b-table-column width="160px" centered field="insoleFill" :label="$translate('insoleFillMat') | capitalize">
              <b-tooltip :label="props.row.insoleFillTag" position="is-left">
                <img
                  v-if="props.row.insoleFillImage"
                  :src="props.row.insoleFillImage"
                  width="75px"
                  height="auto"
                  @click="() => showImageModal(props.row.insoleFillImage, props.row.insoleFillTag)"
                />
              </b-tooltip>
            </b-table-column>
            <b-table-column :label="$translate('thicknesses') | capitalize" centered>
              <b-button class="is-primary" @click="showThicknessesModal(props.row)">
                <b-icon icon="format-list-checkbox"></b-icon>
              </b-button>
            </b-table-column>
          </template>
          <b-table-column
            v-if="$moduleConfiguration[module].modelHasCustomSizes && module !== 'insole'"
            centered
            field="customSizes"
            :label="$translate('custom_sizes') | capitalize"
          >
            {{ formatSizes(props.row) }}
          </b-table-column>
          <template v-if="$moduleConfiguration[module].soles">
            <b-table-column field="sole" :label="$translate('default_sole') | capitalize">
              <b-tooltip :label="props.row.defaultSoleTag" position="is-left">
                <img :src="props.row.defaultSoleImg" width="75px" height="auto" />
              </b-tooltip>
            </b-table-column>
          </template>
          <b-table-column v-if="module === 'insole'" :label="$translate('Ins_I') | capitalize" centered>
            <b-button class="is-primary" @click="showInsertsModal(props.row)">
              <b-icon icon="format-list-checkbox"></b-icon>
            </b-button>
          </b-table-column>
          <b-table-column :label="$translate('action') | capitalize" style="width:100px;vertical-align:middle;">
            <a
              v-if="canManage"
              @click="
                $router.push({
                  name: 'editMod',
                  params: { id: props.row.id, module },
                })
              "
            >
              <b-icon icon="pencil-outline"></b-icon>
            </a>
            <a v-if="canManage" @click="copymodel(props.row.id)">
              <b-icon icon="content-copy"></b-icon>
            </a>
            <a v-if="canManage" @click="deleteOccurence(props.row.id)">
              <b-icon icon="delete-outline"></b-icon>
            </a>
          </b-table-column>
        </template>
        <template v-if="!disableRequestMore" slot="footer">
          <b-button expanded @click="appendData()">
            <b-icon icon="arrow-down-thick"></b-icon>
          </b-button>
        </template>
        <template slot="empty">
          <section v-if="!tableOptions.loading" class="section">
            <div class="content has-text-grey has-text-centered">
              <p>
                <b-icon icon="emoticon-sad" size="is-large"> </b-icon>
              </p>
            </div>
          </section>
        </template>
      </b-table>
      <br /><br />
      <div v-if="module !== 'insole'" class="level">
        <div class="level-left">
          <div class="level-item">
            <div class="card">
              <header class="card-header">
                <p class="card-header-title">
                  {{ $translate('filter') | capitalize }}
                </p>
              </header>
              <div class="card-content">
                <filter-select v-model="catalogTypeFilterInfo" @change="searchData()" />
                <filter-select v-model="shoeTypeFilterInfo" @change="searchData()" />
                <filter-select v-model="seasontagsFilterInfo" @change="searchData()" />
              </div>
            </div>
          </div>
        </div>
      </div>
      <b-modal :active.sync="isImageModalActive" :width="640" scroll="keep">
        <div class="modal-card" style="width: auto">
          <section class="modal-card-body modal-container">
            <img :src="modalImage" />
          </section>
        </div>
      </b-modal>
      <b-modal :active.sync="isThicknessesModalActive" :width="480" scroll="keep">
        <div class="modal-card" style="width: auto">
          <header class="modal-card-head">
            <p class="modal-card-title">{{ $translate('thicknesses') | capitalize }}</p>
          </header>
          <section class="modal-card-body">
            <ul>
              <li v-for="tKey in thicknessKeys" :key="tKey">
                {{ $translate(tKey) | capitalize }}: {{ modalThicknesses[tKey] != null ? modalThicknesses[tKey] : 0 }}
              </li>
            </ul>
          </section>
        </div>
      </b-modal>
      <b-modal :active.sync="isInsertsModalActive" :width="480" scroll="keep">
        <div class="modal-card" style="width: auto">
          <header class="modal-card-head">
            <p class="modal-card-title">{{ $translate('Ins_I') | capitalize }}</p>
          </header>
          <section class="modal-card-body">
            <p v-if="!modalInserts.length">-</p>
            <ul v-else>
              <li v-for="(ins, idx) in modalInserts" :key="idx">
                {{ $translate(ins.section) | capitalize }}{{ ins.side ? ` ${ins.side.toUpperCase()}` : '' }}:
                {{ ins.label }} ({{ ins.thickness }} mm)
              </li>
            </ul>
          </section>
        </div>
      </b-modal>
    </div>
  </section>
</template>

<script>
import materialTools from '../../helpers/mixins/materialTools'
//import keyWordGenerator from "@/helpers/mixins/keyWordGenerator.js";

import mainMixin from '@/helpers/mixins/mainMixin'
import permissionMixin from '@/helpers/mixins/permissionMixin'
import filterSelect from '@/components/FilterSelect'
import jsPDF from 'jspdf'
import allacciatura from '@/views/Mod/components/allacciatura.vue'

export default {
  name: 'Modsmods',
  components: {
    'filter-select': filterSelect,
  },
  mixins: [permissionMixin, mainMixin, materialTools /*, keyWordGenerator*/],
  props: {
    module: {
      required: true,
      type: String,
    },
  },
  data() {
    return {
      catalogTypeFilterInfo: {
        label: 'catalogType',
        options: [{ name: '*' }],
        filterValue: '*',
      },
      shoeTypeFilterInfo: {
        label: 'shoeType',
        options: [{ name: '*' }],
        filterValue: '*',
      },
      seasontagsFilterInfo: {
        label: 'seasontags',
        options: [{ name: '*' }],
        filterValue: '*',
      },
      field: 'name',
      order: 'asc',
      lastRequested: null,
      disableRequestMore: true,
      limit: 20,
      tableData: [],
      queryString: '',
      pells: [],
      accs: [],
      puns: [],
      mors: [],
      mats: [],
      sugs: [],
      soles: [],
      allacci: [],
      rips: [],
      soleIds: [],
      allacciIds: [],
      insoleBases: [],
      insoleCovers: [],
      insoleFills: [],
      inserts: [],
      mainQuery: null,
      key: 0,
      isImageModalActive: false,
      modalImage: '',
      modalTag: '',
      isThicknessesModalActive: false,
      isInsertsModalActive: false,
      modalThicknesses: {},
      modalInserts: [],
      thicknessKeys: ['heel_height', 'ball_height', 'fings_height', 'sup_col_med', 'avvolgenza_mediale'],
    }
  },
  computed: {
    allacciatura() {
      return allacciatura
    },
    canManage() {
      return (
        this.userInfo.type === 'A' ||
        (this.userInfo.type === 'I' && (this.userInfo.level === 'S' || this.userInfo.level === '2'))
      )
    },
  },
  created() {
    this.mainQuery = this.$db.collection(this.$moduleConfiguration[this.module].models)
  },

  async mounted() {
    this.tableOptions.loading = true
    let isAllowed = false
    switch (this.userInfo.type) {
      case 'A':
        isAllowed = true
        break
      case 'I':
        isAllowed = true
        break
    }
    if (!isAllowed) {
      await this.$router.push('/forbidden')
      return
    }
    await this.loadAsyncData(this.module)
  },
  methods: {
    formatSizes(row) {
      if (!row.sizes) return '-'
      return row.sizes.min + ' - ' + row.sizes.max
    },
    sortPressed(field, order) {
      this.field = field
      this.order = order
      this.searchData()
    },
    copymodel(idtocopy) {
      this.$router.push({
        name: 'newMod',
        params: { idtocopy, module: this.module },
      })
    },
    createFilters() {
      this.catalogTypeFilterInfo.options = [{ name: '*' }]
      this.shoeTypeFilterInfo.options = [{ name: '*' }]
      this.seasontagsFilterInfo.options = [{ name: '*' }]

      this.$availableSexType.forEach(element => {
        this.catalogTypeFilterInfo.options.push({
          name: element,
        })
      })

      this.$availableShoeType.forEach(element => {
        this.shoeTypeFilterInfo.options.push({
          name: element,
        })
      })

      this.$seasontag.forEach(element => {
        this.seasontagsFilterInfo.options.push({
          name: element,
        })
      })
    },
    searchData() {
      this.tableData = []
      this.tableOptions.loading = true
      this.mainQuery = this.$db.collection(this.$moduleConfiguration[this.module].models)
      if (this.field) {
        this.mainQuery = this.mainQuery.orderBy(this.field, this.order)
      }
      this.mainQuery = this.mainQuery.where('searchArray', 'array-contains', this.queryString.toLowerCase())
      if (this.catalogTypeFilterInfo.filterValue !== '*') {
        this.mainQuery = this.mainQuery.where('catalogType', '==', this.catalogTypeFilterInfo.filterValue)
      }
      if (this.shoeTypeFilterInfo.filterValue !== '*') {
        this.mainQuery = this.mainQuery.where('shoeType', '==', this.shoeTypeFilterInfo.filterValue)
      }
      if (this.seasontagsFilterInfo.filterValue !== '*') {
        this.mainQuery = this.mainQuery.where('seasontags', '==', this.seasontagsFilterInfo.filterValue)
      }

      this.mainQuery
        .limit(this.limit + 1)
        .get()
        .then(docs => {
          this.disableRequestMore = docs.size <= this.limit
          let i = 0
          docs.forEach(doc => {
            let newModel = doc.data()
            newModel.id = doc.id
            newModel = this.arrichData(doc)
            i++
            if (i <= this.limit) {
              this.tableData.push(newModel)
            }
            this.lastRequested = doc
          })
          this.tableOptions.loading = false
        })
    },

    deleteOccurence(idtoerase) {
      this.$buefy.dialog.confirm({
        title: this.$translate('attention'),
        message: this.$translate('model_message_destroy'),
        cancelText: this.$translate('undo'),
        confirmText: this.$translate('model_message_confirm'),
        type: 'is-danger',
        hasIcon: true,
        onConfirm: () =>
          this.$db
            .collection(this.$moduleConfiguration[this.module].models)
            .doc(idtoerase)
            .delete()
            .then(() => {
              this.searchData()
              this.$buefy.notification.open({
                duration: 3000,
                message: this.$translate('success_delete'),
              })
            }),
      })
    },
    appendData() {
      let thisQuery = this.$db.collection(this.$moduleConfiguration[this.module].models)
      if (this.lastRequested != null) {
        thisQuery = thisQuery.startAt(this.lastRequested)
      }

      thisQuery
        .limit(this.limit + 1)
        .get()
        .then(docs => {
          // Fa comparire o scomparire il pulsante in fondo per caricare più modelli nella pagina
          this.disableRequestMore = docs.size <= this.limit

          let i = 0
          docs.forEach(doc => {
            let newModel = doc.data()
            newModel.id = doc.id
            newModel = this.arrichData(doc)
            i++
            if (i <= this.limit) {
              this.tableData.push(newModel)
            }
            this.lastRequested = doc
          })
          this.tableOptions.loading = false
          this.tableData.sort((a, b) => a.name.localeCompare(b.name))
        })
    },
    async loadAsyncData(module) {
      this.tableOptions.loading = true
      this.createFilters()
      this.tableData = []
      switch (module) {
        case 'tailored':
          await this.loadAsyncDataTailored()
          break
        case 'fits':
          await this.loadAsyncDataFits()
          break
        case 'modular':
          await this.loadAsyncDataModular()
          break
        case 'insole':
          await this.loadAsyncDataInsole()
      }
    },
    async loadAsyncDataTailored() {
      const materialsText = ['Pel', 'Acc', 'Pun', 'Mor', 'Mat', 'Sug']
      this.getMaterialsArray(materialsText).then(arraysresponse => {
        this.pells = arraysresponse[0]
        this.accs = arraysresponse[1]
        this.puns = arraysresponse[2]
        this.mors = arraysresponse[3]
        this.mats = arraysresponse[4]
        this.sugs = arraysresponse[5]
        this.appendData()
      })
    },
    async loadAsyncDataFits() {
      this.tableOptions.loading = true
      this.createFilters()
      this.tableData = []
      const materialsText = ['Pel_F', 'Acc_F', 'All_F', 'Pun_F', 'soles']
      this.getMaterialsArray(materialsText).then(arraysresponse => {
        this.pells = arraysresponse[0]
        this.accs = arraysresponse[1]
        this.allacci = arraysresponse[2]
        this.puns = arraysresponse[3]
        this.soles = arraysresponse[4]
        this.appendData()
      })
    },
    async loadAsyncDataModular() {
      this.tableOptions.loading = true
      this.createFilters()
      this.tableData = []
      const materialsText = ['Pel_M', 'Rip_M', 'Acc_M', 'All_M', 'Pun_M', 'soles_M']
      this.getMaterialsArray(materialsText).then(arraysresponse => {
        this.pells = arraysresponse[0]
        this.rips = arraysresponse[1]
        this.accs = arraysresponse[2]
        this.allacci = arraysresponse[3]
        this.puns = arraysresponse[4]
        this.soles = arraysresponse[5]
        this.appendData()
      })
    },
    async loadAsyncDataInsole() {
      this.tableOptions.loading = true
      this.tableData = []
      const materialsText = ['Moi_I', 'Ric_plan_I', 'Rie_plan_I', 'Ins_I']
      this.getMaterialsArray(materialsText).then(arraysresponse => {
        this.insoleBases = arraysresponse[0]
        this.insoleCovers = arraysresponse[1]
        this.insoleFills = arraysresponse[2]
        this.inserts = arraysresponse[3]
        this.appendData()
      })
    },
    async loadMaterialsId() {
      const materialsIdText = ['All_F', 'soles']
      this.getMaterialsIdArray(materialsIdText).then(arrayresponse => {
        this.allacciId = arrayresponse[0]
        this.soleIds = arrayresponse[1]
      })
    },
    exportPdfTable(module) {
      switch (module) {
        case 'tailored':
          this.exportPdfTableTailored()
          break
        case 'fits':
          this.exportPdfTableFits()
          break
        case 'modular':
          this.exportPdfTableModular()
          break
        case 'insole':
          this.exportPdfTableInsole()
      }
    },
    exportPdfTableTailored() {
      let data = []
      this.tableOptions.checkedRows.forEach(row => {
        let newRow = {}
        newRow[this.$options.filters.capitalize(this.$translate('name'))] = row.name
        let pellRow = ''
        for (let i = 1; i <= row.pelCount; i++) {
          pellRow += (row[`pell${i}tag`] == undefined ? '' : row[`pell${i}tag`]) + '\n'
        }
        newRow[this.$options.filters.capitalize(this.$translate('pelltitle'))] = pellRow

        newRow[this.$options.filters.capitalize(this.$translate('fodera'))] =
          (row.fod1tag == undefined ? '' : row.fod1tag) + '\n' + (row.fod2tag == undefined ? '' : row.fod2tag)

        let allacciaturatext = ''
        for (let i = 0; i < row.allacci.length; i++) {
          allacciaturatext +=
            this.$translate(row.allacci[i].allprim) + ' ' + this.$translate(row.allacci[i].allvar) + '\n'
        }
        newRow[this.$options.filters.capitalize(this.$translate('allacciaturatitle'))] = allacciaturatext
        newRow[this.$options.filters.capitalize(this.$translate('tacc'))] =
          (row.tacc == undefined ? '' : this.$options.filters.capitalize(this.$translate(row.tacc))) +
          ' ' +
          (row.taccvar == undefined ? '' : this.$options.filters.capitalize(this.$translate(row.taccvar)))
        newRow[this.$options.filters.capitalize(this.$translate('suola'))] =
          (row.battag == undefined ? '' : row.battag) +
          '\n' +
          (row.tacczepptag == undefined ? '' : row.tacczepptag) +
          '\n' +
          (row.inttag == undefined ? '' : row.inttag)
        newRow[this.$options.filters.capitalize(this.$translate('mountag'))] =
          (row.riftype == undefined ? '' : this.$options.filters.capitalize(this.$translate(row.riftype))) +
          ' ' +
          (row.mountag == undefined ? '' : this.$options.filters.capitalize(this.$translate(row.mountag)))
        let rifintext = ''
        let tempmat = {}
        let tempvar = {}

        switch (row.riftype) {
          case 'rifinitura0':
            tempmat = this.mats.find(temp => temp.id == row.guardmat)
            if (tempmat !== undefined) {
              tempvar = tempmat.variants.find(temp => temp.id == row.guardvar)
              if (tempvar !== undefined) {
                rifintext = this.$translateMaterial(tempmat) + '-' + tempvar.name
              }
            }
            break
          case 'rifinitura1':
            tempmat = this.sugs.find(temp => temp.id == row.fascmat)
            if (tempmat !== undefined) {
              tempvar = tempmat.variants.find(temp => temp.id == row.fascvar)
              if (tempvar !== undefined) {
                rifintext = this.$translateMaterial(tempmat) + '-' + tempvar.name
              }
            }

            break
          default:
            rifintext = ''
            break
        }
        newRow[this.$options.filters.capitalize(this.$translate('rifinitura0'))] = rifintext
        data.push(Object.assign({}, newRow))
      })
      let keys = [
        {
          name: this.$options.filters.capitalize(this.$translate('name')),
          width: 80,
        },
        {
          name: this.$options.filters.capitalize(this.$translate('pelltitle')),
          width: 140,
        },
        {
          name: this.$options.filters.capitalize(this.$translate('fodera')),
          width: 140,
        },
        {
          name: this.$options.filters.capitalize(this.$translate('allacciaturatitle')),
          width: 102,
        },
        {
          name: this.$options.filters.capitalize(this.$translate('tacc')),
          width: 102,
        },
        {
          name: this.$options.filters.capitalize(this.$translate('suola')),
          width: 102,
        },
        {
          name: this.$options.filters.capitalize(this.$translate('mountag')),
          width: 82,
        },
        {
          name: this.$options.filters.capitalize(this.$translate('rifinitura0')),
          width: 102,
        },
      ]

      let headers = []
      for (var i = 0; i < keys.length; i += 1) {
        headers.push({
          id: keys[i].name,
          name: keys[i].name,
          prompt: keys[i].name,
          align: 'center',
          width: keys[i].width,
          padding: 0,
        })
      }

      var doc = new jsPDF('l', 'px', 'a4')
      doc.table(1, 1, data, headers, { padding: 0, fontSize: 6 })

      doc.save(
        `Lista${this.$store.getters.user.userInfo.name}${
          this.$store.getters.user.userInfo.surname
        }${new Date().toLocaleDateString()}.pdf`
      )
    },
    exportPdfTableFits() {
      this.getMaterialsIdArray()
      let data = []
      this.tableOptions.checkedRows.forEach(row => {
        let newRow = {}
        newRow[this.$options.filters.capitalize(this.$translate('name'))] = row.name
        newRow[this.$options.filters.capitalize(this.$translate('pelltitle'))] =
          (row.pell1tag == undefined ? '' : row.pell1tag) +
          '\n' +
          (row.pell2tag == undefined ? '' : row.pell2tag) +
          '\n' +
          (row.pell3tag == undefined ? '' : row.pell3tag) +
          '\n' +
          (row.pell4tag == undefined ? '' : row.pell4tag)

        let accText = ''
        for (let fodera of row.accTags) {
          accText += `${fodera} \n`
        }
        newRow[this.$options.filters.capitalize(this.$translate('fodera'))] = accText

        let allText = ''
        for (let allacciatura of row.allTags) {
          allText += `${allacciatura} \n`
        }
        newRow[this.$options.filters.capitalize(this.$translate('allacciaturatitle'))] = allText

        let solesText = ''
        for (let sole of row.soleTags) {
          solesText += `${sole} \n`
        }
        newRow[this.$options.filters.capitalize(this.$translate('suola'))] = solesText

        data.push(Object.assign({}, newRow))
      })

      let keys = [
        {
          name: this.$options.filters.capitalize(this.$translate('name')),
          width: 80,
        },
        {
          name: this.$options.filters.capitalize(this.$translate('pelltitle')),
          width: 140,
        },
        {
          name: this.$options.filters.capitalize(this.$translate('fodera')),
          width: 140,
        },
        {
          name: this.$options.filters.capitalize(this.$translate('allacciaturatitle')),
          width: 102,
        },
        {
          name: this.$options.filters.capitalize(this.$translate('suola')),
          width: 102,
        },
      ]

      let headers = []
      for (let i = 0; i < keys.length; i += 1) {
        headers.push({
          id: keys[i].name,
          name: keys[i].name,
          prompt: keys[i].name,
          align: 'center',
          width: keys[i].width,
          padding: 0,
        })
      }

      let doc = new jsPDF('l', 'px', 'a4')
      doc.table(1, 1, data, headers, { padding: 0, fontSize: 6 })

      doc.save(
        `Lista${this.$store.getters.user.userInfo.name}${
          this.$store.getters.user.userInfo.surname
        }${new Date().toLocaleDateString()}.pdf`
      )
    },
    exportPdfTableModular() {
      this.getMaterialsIdArray()
      let data = []
      this.tableOptions.checkedRows.forEach(row => {
        let newRow = {}
        newRow[this.$options.filters.capitalize(this.$translate('name'))] = row.name
        let pellRow = ''
        for (let i = 1; i <= row.pelCount; i++) {
          pellRow += row[`pell${i}tag`] === undefined ? '' : row[`pell${i}tag`]
          if (i < row.pelCount) pellRow += '\n'
        }
        newRow[this.$options.filters.capitalize(this.$translate('pelltitle'))] = pellRow

        let ripRow = ''
        for (let i = 1; i <= row.ripCount; i++) {
          ripRow += row[`rip${i}tag`] === undefined ? '' : row[`rip${i}tag`]
          if (i < row.ripCount) ripRow += '\n'
        }
        newRow[this.$options.filters.capitalize(this.$translate('riptitle'))] = ripRow

        newRow[this.$options.filters.capitalize(this.$translate('custom_sizes'))] = row.sizes
          ? row.sizes.min + ' - ' + row.sizes.max
          : '-'

        let solesText = ''
        for (let sole of row.soleTags) {
          solesText += `${sole} \n`
        }
        newRow[this.$options.filters.capitalize(this.$translate('suola'))] = solesText

        data.push(Object.assign({}, newRow))
      })

      let keys = [
        {
          name: this.$options.filters.capitalize(this.$translate('name')),
          width: 80,
        },
        {
          name: this.$options.filters.capitalize(this.$translate('pelltitle')),
          width: 140,
        },
        {
          name: this.$options.filters.capitalize(this.$translate('riptitle')),
          width: 140,
        },
        {
          name: this.$options.filters.capitalize(this.$translate('custom_sizes')),
          width: 102,
        },
        {
          name: this.$options.filters.capitalize(this.$translate('suola')),
          width: 102,
        },
      ]

      let headers = []
      for (let i = 0; i < keys.length; i += 1) {
        headers.push({
          id: keys[i].name,
          name: keys[i].name,
          prompt: keys[i].name,
          align: 'center',
          width: keys[i].width,
          padding: 0,
        })
      }

      let doc = new jsPDF('l', 'px', 'a4')
      doc.table(1, 1, data, headers, { padding: 0, fontSize: 6 })

      doc.save(
        `Lista${this.$store.getters.user.userInfo.name}${
          this.$store.getters.user.userInfo.surname
        }${new Date().toLocaleDateString()}.pdf`
      )
    },
    exportPdfTableInsole() {
      // implement me
    },
    arrichData(doc) {
      let newmod = doc.data()
      newmod.id = doc.id
      newmod.accTags = []
      newmod.allTags = []
      newmod.soleTags = []
      const { maxPel, maxRip } = this.$moduleConfiguration[this.module]
      let tempmat, tempvar
      let pelCount = 0
      let ripCount = 0

      for (let i = 0; i <= maxPel; i++) {
        let tempmat = this.pells.find(temp => temp.id === newmod[`pell${i}mat`])
        let tempvar = {}
        if (tempmat !== undefined) {
          tempvar = tempmat.variants.find(temp => temp.id == newmod[`pell${i}var`])
          if (tempvar !== undefined) {
            newmod[`pellImage${i}`] = tempvar.image
            newmod[`pell${i}tag`] = this.$translateMaterial(tempmat) + '-' + tempvar.name
            if (i > pelCount) pelCount = i
          }
        }
      }
      newmod.pelCount = pelCount

      for (let i = 0; i <= maxRip; i++) {
        let tempmat = this.rips.find(temp => temp.id === newmod[`rip${i}mat`])
        let tempvar = {}
        if (tempmat !== undefined) {
          tempvar = tempmat.variants.find(temp => temp.id === newmod[`rip${i}var`])
          if (tempvar !== undefined) {
            newmod[`ripImage${i}`] = tempvar.image
            newmod[`rip${i}tag`] = this.$translateMaterial(tempmat) + '-' + tempvar.name
            if (i > ripCount) ripCount = i
          }
        }
      }
      newmod.ripCount = ripCount

      if (this.module === 'fits') {
        newmod.fodmat.forEach(fodera => {
          tempmat = this.accs.find(temp => temp.id === fodera.material)
          if (tempmat !== undefined) {
            tempvar = tempmat.variants.find(temp => temp.id === fodera.variant)
            if (tempvar !== undefined) {
              newmod.accTags.push(this.$translateMaterial(tempmat) + '-' + tempvar.name)
            }
          }
        })
      }

      if (this.$moduleConfiguration[this.module].modelHasAllacciatura) {
        newmod.allacci.forEach(allacciatura => {
          tempmat = this.allacci.find(temp => temp.id === allacciatura)
          if (tempmat !== undefined) {
            newmod.allTags.push(this.$translateMaterial(tempmat))
          }
        })
      }

      if (this.$moduleConfiguration[this.module].soles) {
        tempmat = this.soles.find(temp => temp.id === newmod.sole_default)
        if (tempmat !== undefined) {
          newmod.defaultSoleImg = tempmat.image
          newmod.defaultSoleTag = this.$translateMaterial(tempmat) + '-' + this.$translateMaterial(tempmat.color)
        }

        ;(newmod.soles || []).forEach(sole => {
          tempmat = this.soles.find(temp => temp.id === sole)
          if (tempmat !== undefined) {
            newmod.soleTags.push(this.$translateMaterial(tempmat) + '-' + this.$translateMaterial(tempmat.color))
          }
        })
      }

      if (this.module === 'tailored') {
        tempmat = this.mors.find(temp => temp.id === newmod.tacczepp)
        if (tempmat !== undefined) {
          tempvar = tempmat.variants.find(temp => temp.id === newmod.tacczeppvar)
          if (tempvar !== undefined) {
            newmod.tacczeppImage = tempvar.image
            newmod.tacczepptag = this.$translateMaterial(tempmat) + '-' + tempvar.name
          }
        }

        tempmat = this.mors.find(temp => temp.id === newmod.intemat)
        if (tempmat !== undefined) {
          tempvar = tempmat.variants.find(temp => temp.id === newmod.intevar)
          if (tempvar !== undefined) {
            newmod.intImage = tempvar.image
            newmod.inttag = this.$translateMaterial(tempmat) + '-' + tempvar.name
          }
        }
        tempmat = this.accs.find(temp => temp.id === newmod.fodantmat)
        if (tempmat !== undefined) {
          tempvar = tempmat.variants.find(temp => temp.id === newmod.fodantvar)
          if (tempvar !== undefined) {
            newmod.fodImage1 = tempvar.image
            newmod.fod1tag = this.$translateMaterial(tempmat) + '-' + tempvar.name
          }
        }
        tempmat = this.accs.find(temp => temp.id === newmod.fodposmat)
        if (tempmat !== undefined) {
          tempvar = tempmat.variants.find(temp => temp.id === newmod.fodposvar)
          if (tempvar !== undefined) {
            newmod.fodImage2 = tempvar.image
            newmod.fod2tag = this.$translateMaterial(tempmat) + '-' + tempvar.name
          }
        }

        tempmat = this.puns.find(temp => temp.id === newmod.battmat)
        if (tempmat !== undefined) {
          tempvar = tempmat.variants.find(temp => temp.id === newmod.battvar)
          if (tempvar !== undefined) {
            newmod.batImage = tempvar.image
            newmod.battag = this.$translateMaterial(tempmat) + '-' + tempvar.name
          }
        }
      }

      if (this.module === 'insole') {
        tempmat = this.insoleBases.find(temp => temp.id === newmod.insoleBaseMat)
        if (tempmat !== undefined) {
          tempvar = tempmat.variants.find(temp => temp.id === newmod.insoleBaseVar)
          if (tempvar !== undefined) {
            newmod.insoleBaseImage = tempvar.image
            newmod.insoleBaseTag = this.$translateMaterial(tempmat) + '-' + tempvar.name
          }
        }
        tempmat = this.insoleCovers.find(temp => temp.id === newmod.insoleCoverMat)
        if (tempmat !== undefined) {
          tempvar = tempmat.variants.find(temp => temp.id === newmod.insoleCoverVar)
          if (tempvar !== undefined) {
            newmod.insoleCoverImage = tempvar.image
            newmod.insoleCoverTag = this.$translateMaterial(tempmat) + '-' + tempvar.name
          }
        }
        tempmat = this.insoleFills.find(temp => temp.id === newmod.insoleFillMat)
        if (tempmat !== undefined) {
          tempvar = tempmat.variants.find(temp => temp.id === newmod.insoleFillVar)
          if (tempvar !== undefined) {
            newmod.insoleFillImage = tempvar.image
            newmod.insoleFillTag = this.$translateMaterial(tempmat) + '-' + tempvar.name
          }
        }
        newmod.insertsList = []
        if (newmod.inserts) {
          Object.keys(newmod.inserts)
            .sort()
            .forEach(key => {
              if (!Array.isArray(newmod.inserts[key])) return
              const side = key.endsWith('_sx') ? 'sx' : key.endsWith('_dx') ? 'dx' : ''
              const section = side ? key.slice(0, -3) : key
              newmod.inserts[key].forEach(slot => {
                if (slot.material == null || slot.variant == null) return
                const insertMat = this.inserts.find(temp => temp.id === slot.material)
                if (insertMat !== undefined) {
                  const insertVar = insertMat.variants.find(temp => temp.id === slot.variant)
                  if (insertVar !== undefined) {
                    newmod.insertsList.push({
                      section,
                      side,
                      label: this.$translateMaterial(insertMat) + '-' + insertVar.name,
                      thickness: slot.thickness,
                    })
                  }
                }
              })
            })
        }
      }

      return newmod
    },
    showImageModal(img, tag) {
      this.modalImage = img
      this.modalTag = tag
      this.isImageModalActive = true
    },
    showThicknessesModal(row) {
      this.modalThicknesses = row.thicknesses || {}
      this.isThicknessesModalActive = true
    },
    showInsertsModal(row) {
      this.modalInserts = row.insertsList || []
      this.isInsertsModalActive = true
    },
  },
}
</script>
<style scoped>
.container >>> .b-table .table th {
  font-weight: 600;
  position: sticky;
  top: 0px;
  background: white;
  z-index: 10;
}

.container >>> .b-tooltip:not([data-label='']):hover:before,
.container >>> .b-tooltip:not([data-label='']):hover:after {
  z-index: 1;
}
.container >>> .table-footer td {
  color: #bdbdbd;
}
.pellami-container {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.modal-container {
  display: flex;
  justify-content: center;
}
</style>
